/**
 * ⚖ The S3 composition gate's RENDER + BLOCK rule (spec: §4-ter of
 * `ai-docs/lexiun-agent-specialists-prompt-base.md`).
 *
 * WHAT IT GATES, precisely: the **production LABEL MOVE** of a
 * `harn:sp-<name>` persona — making a version the one every request resolves.
 * Not the enable verb, and not authoring: a candidate may be written and
 * revised freely, because writing one changes nothing that serves traffic.
 * (§4-ter amendment, relayed 2026-08-21: "`enable` is the production LABEL
 * move, so the gate belongs on promotion and authoring a candidate stays
 * ungated.")
 *
 * ⚠ FAIL-CLOSED IS THE WHOLE DESIGN. Every state that is not an affirmative
 * recorded `pass` BLOCKS. That includes the two that are tempting to wave
 * through:
 *
 *   - `unread` — we could not read the verdict. A gate that opens when it
 *     cannot see is not a gate. §4-ter is explicit that the checker itself
 *     fails closed rather than returning a silent pass; the UI over it must
 *     not be the layer that reintroduces the hole.
 *   - `unchecked` — `verdict: null`, the honest third state: no verdict
 *     recorded for this triple YET. Never pass, never failure. The trap is
 *     the same one the jobs panel already paid for: one value, two readings,
 *     and only one of them is alarming.
 *
 * ⚠ AND THE STALENESS DOCTRINE IS THE SERVER'S, NOT OURS. A verdict is recorded
 * against the triple `(baseVersion, manifestHash, personaVersion)`; any
 * component changing VOIDS the pass. The client never decides a recorded pass
 * is still good — it asks for the CURRENT triple and renders what comes back.
 * A base-prompt edit therefore voids all ten specialists' passes, by design.
 */

import type { CompositionVerdict, TierAFinding, TierBFinding } from "./specialists-api";

/**
 * The specialist id behind a prompt slug, or `null` if this slug is not a
 * specialist persona.
 *
 * ⚠ Deliberately strict. Only `harn:sp-<name>` is gated; `harn:specialist-base`
 * is NOT (it is the base, not a persona — and gating it would be circular,
 * since the gate reads the base as its own input). Every other prompt in the
 * manager promotes exactly as it always has: a gate that silently widened its
 * own scope would break unrelated prompt operations with a specialists error.
 */
export function specialistIdFromSlug(slug: string): string | null {
  const m = /^harn:(sp-[a-z0-9][a-z0-9-]*)$/.exec(slug.trim());
  return m ? m[1] : null;
}

export type GateState =
  /** Not a specialist persona — no gate applies, promotion is unchanged. */
  | { kind: "not-a-specialist" }
  /** The verdict could not be read. BLOCKS: a gate that cannot see is not a gate. */
  | { kind: "unread"; reason: string }
  /** ⚖ The manifest has not been reported. An AVAILABILITY block, never a
   *  persona defect — the author has nothing to fix. */
  | { kind: "unavailable"; reason: string }
  /** `verdict: null` — no verdict recorded for this triple yet. */
  | { kind: "unchecked" }
  | { kind: "pass"; checkedAt: string | null; advisories: Advisory[] }
  | {
      kind: "fail";
      /** When this verdict was recorded. Shown as a FACT, never judged — see
       *  the note on `checkedAt` below. */
      checkedAt: string | null;
      tierA: TierAFinding[];
      tierB: TierBFinding[];
      tierBSkipped?: string;
    }
  | {
      kind: "incomplete";
      reason: string;
      tierA: TierAFinding[];
      tierB: TierBFinding[];
      tierBSkipped?: string;
    };

/*
 * ⚠ WHY `checkedAt` IS SHOWN ON A FAIL, AND WHY IT IS NEVER JUDGED.
 *
 * The judge is NON-DETERMINISTIC on marginal passages — §4-ter says so in the
 * abstract, and the spec owner measured it on 2026-08-21: a v6 run produced a
 * finding IN A DIFFERENT KIND on text two earlier passing versions also
 * contained. So a recorded verdict is one draw, and knowing when it was drawn
 * is real context for an operator deciding whether to re-run.
 *
 * ⚠ But it is a FACT, not a verdict: §4-ter's no-flapping clause makes the
 * recorded verdict GOVERN until the triple changes, so age does not weaken it
 * and there is no cadence to judge it against. Rendering "stale" here would be
 * an alarm that cannot be right — the same rule the roster's `Reported` column
 * is built on, and the same one the jobs panel had to learn.
 */

/** An advisory rides along with a pass — rendered, never blocking (§4-ter). */
export interface Advisory {
  assertion: string;
  detail: string;
}

/** The lexserver code meaning "no runtime has reported this manifest". */
export const MANIFEST_UNKNOWN = "COMPOSITION_MANIFEST_UNKNOWN";

/**
 * Turn a settled read of the verdict endpoint into the state to render.
 *
 * `errorCode`/`errorMessage` describe a REJECTED read. `MANIFEST_UNKNOWN` (409)
 * is separated from every other failure because the operator action differs
 * completely: an availability block is somebody rolling a runtime, a read
 * failure is somebody fixing the console's access, and a persona defect is the
 * author editing their text. Collapsing them sends people to the wrong work.
 */
export function selectGateState(
  slug: string,
  read:
    | { ok: true; value: CompositionVerdict }
    | { ok: false; code?: string; error: string }
    | null,
): GateState {
  if (specialistIdFromSlug(slug) === null) return { kind: "not-a-specialist" };
  if (!read) return { kind: "unread", reason: "not read yet" };

  if (!read.ok) {
    if (read.code === MANIFEST_UNKNOWN) {
      return {
        kind: "unavailable",
        reason: read.error?.trim() || "no runtime has reported this specialist's manifest",
      };
    }
    return { kind: "unread", reason: read.error?.trim() || "the read failed without a stated reason" };
  }

  const v = read.value;
  const tierA = v.tierA ?? [];
  const tierB = v.tierB ?? [];

  switch (v.verdict) {
    case "pass":
      return { kind: "pass", checkedAt: v.checkedAt ?? null, advisories: advisoriesOf(tierA, tierB) };
    case "fail":
      return { kind: "fail", checkedAt: v.checkedAt ?? null, tierA, tierB, tierBSkipped: v.tierBSkipped };
    case "incomplete":
      return {
        kind: "incomplete",
        reason: v.incompleteReason?.trim() || "reported incomplete, without a stated reason",
        tierA,
        tierB,
        tierBSkipped: v.tierBSkipped,
      };
    case null:
    case undefined:
      return { kind: "unchecked" };
    default:
      // A verdict this client has no rendering for. Blocks, like everything
      // that is not an affirmative pass — never assumed to be a pass.
      return {
        kind: "incomplete",
        reason: `the server reported an unrecognised verdict '${String(v.verdict)}'`,
        tierA,
        tierB,
      };
  }
}

/** Advisories ride WITH a pass: rendered, never blocking (§4-ter). */
function advisoriesOf(tierA: TierAFinding[], tierB: TierBFinding[]): Advisory[] {
  return [
    ...tierA
      .filter((f) => f.verdict === "advisory")
      .map((f) => ({ assertion: f.assertion, detail: f.excerpt ?? "" })),
    ...tierB
      .filter((f) => f.severity === "advisory")
      .map((f) => ({ assertion: f.assertion, detail: f.rationale ?? "" })),
  ];
}

/**
 * May this version be promoted?
 *
 * ⚠ ONLY an affirmative recorded `pass` opens the gate. Written as an
 * allow-list rather than a block-list on purpose: a block-list grants passage
 * to any state added later that nobody remembered to add to it, and the
 * failure would be a silent approval — the worst direction for this surface.
 */
export function blocksPromotion(state: GateState): boolean {
  return state.kind !== "not-a-specialist" && state.kind !== "pass";
}

/** One operator-facing sentence for why the button is disabled. */
export function blockReason(state: GateState): string | null {
  switch (state.kind) {
    case "not-a-specialist":
    case "pass":
      return null;
    case "unread":
      return `The composition gate could not be read (${state.reason}), so promotion is blocked. This is NOT a verdict on the persona — the gate fails closed rather than guessing.`;
    case "unavailable":
      return `Blocked on AVAILABILITY, not on this persona: ${state.reason}. Nothing in the text needs fixing — a runtime has to report its manifest before the composition can be judged.`;
    case "unchecked":
      return "No composition check has been recorded for this exact base + manifest + persona combination. Run the check to promote.";
    case "fail":
      return "The composition check FAILED for this combination. The findings below quote both sides; promotion stays blocked until they are resolved and the check is re-run.";
    case "incomplete":
      return `The composition check could not complete (${state.reason}), so promotion is blocked. An incomplete check is not a pass.`;
  }
}


/**
 * Refuse to look up a verdict without the base version — or `null` to proceed.
 *
 * ⚠ THIS EXISTS BECAUSE THE WIRE DEFAULTS IT SILENTLY. lexserver's verdict
 * route reads `Number(query.baseVersion ?? 0)`, so an omitted param looks up
 * the triple `(0, hash, personaVersion)` — which no judge run can ever have
 * recorded — and answers **200 with `verdict: null`**: a legitimate-looking
 * "not yet checked" for a question that cannot be answered. The gate then
 * blocks forever, reporting a state that is FALSE, and no error appears
 * anywhere. Fail-closed kept that merely broken rather than dangerous; it did
 * not make it correct.
 *
 * The lesson generalises past this route: **a default supplied for half a
 * compound key does not degrade the query, it silently redirects it** — at
 * something that reliably does not exist, which reads as a clean negative.
 */
export function requireBaseVersion(
  baseVersion: number | null | undefined,
): { ok: false; error: string } | null {
  if (typeof baseVersion === "number" && Number.isInteger(baseVersion) && baseVersion > 0) {
    return null;
  }
  return {
    ok: false,
    error:
      "the production version of harn:specialist-base could not be determined, and it is half the lookup key",
  };
}


/**
 * What to tell an operator when the judge REQUEST did not return.
 *
 * ⚠ A FAILED REQUEST IS NOT A FAILED CHECK, and on this door the difference is
 * operationally real rather than pedantic: the judge is an LLM round trip that
 * can outlive a client timeout, and **the run continues server-side and records
 * its verdict anyway.** (Measured by the spec owner on 2026-08-21: their own
 * client timed out at two minutes on a v6 run that was still executing.)
 *
 * So the honest reading of a thrown POST is *"we stopped listening"*, never
 * *"the check failed"* — and the recorded verdict, not the response body, is
 * the authority. A panel that renders the timeout as a failure invites an
 * operator to re-run a judge that is already running: a second LLM call, and a
 * second recorded verdict for a triple whose first one is still in flight.
 *
 * The response body is a convenience. The re-read is the truth.
 */
export function judgeRequestFailureNotice(error: string): string {
  return (
    `The check request did not return (${error}). This is NOT a verdict — the judge is an LLM ` +
    `call that can outlive the request, and a run that started will record its verdict ` +
    `server-side regardless. The state above was re-read after the failure; if it still says ` +
    `not-yet-checked, wait and refresh rather than re-running, so a second judge call is not ` +
    `spent on a check that may already be finishing.`
  );
}
